/**
 * ExteriorMap — the car's outside as the legacy admin drew it (92 clickable parts), coloured by the severity of
 * each part's current fault. Pure presentation: the modal owns the faults and the selection.
 * Severity colours are tokens: závažná = red, středně závažná = warn, drobná = the yellow row tint.
 */

import type { KeyboardEvent } from 'react';
import { EXTERIOR_PARTS, EXTERIOR_VIEWBOX } from './exterior-map-data';
import type { Zavaznost } from './api';

export const ZAVAZNOST_TEXT: Record<Zavaznost, string> = {
  nizka: 'Drobná',
  stredni: 'Středně závažná',
  vysoka: 'Závažná',
};

/** Fill + stroke per state; a part without a fault is the plain outline. */
export const SEVERITY_FILL: Record<Zavaznost | 'none', string> = {
  none: 'fill-paper stroke-ink-2',
  nizka: 'fill-[var(--row-tint-yellow)] stroke-warn',
  stredni: 'fill-warn stroke-ink-2',
  vysoka: 'fill-red stroke-ink-2',
};

interface ExteriorMapProps {
  faults: ReadonlyMap<number, Zavaznost>;
  selected: number | null;
  onSelect: (id: number) => void;
  /** Frozen (a save is in flight): the selection must not move under the payload. */
  disabled?: boolean;
}

export function ExteriorMap({ faults, selected, onSelect, disabled = false }: ExteriorMapProps) {
  const key = (id: number) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!disabled) onSelect(id);
    }
  };

  return (
    <svg
      viewBox={EXTERIOR_VIEWBOX}
      role="group"
      aria-label="Mapa vnějšku vozidla"
      data-testid="zavady-mapa"
      className="mx-auto block h-auto max-h-[70vh] w-full max-w-[420px]"
    >
      {EXTERIOR_PARTS.map((p) => {
        const severity = faults.get(p.id);
        const common = {
          'data-testid': `dil-${p.id}`,
          'data-zavaznost': severity ?? 'none',
          'aria-label': p.title + (severity ? ` — ${ZAVAZNOST_TEXT[severity]}` : ''),
          'aria-pressed': selected === p.id,
          role: 'button' as const,
          tabIndex: disabled ? -1 : 0,
          onClick: () => !disabled && onSelect(p.id),
          onKeyDown: key(p.id),
          strokeWidth: selected === p.id ? 60 : 20,
          className: [
            SEVERITY_FILL[severity ?? 'none'],
            selected === p.id ? '!stroke-accent' : '',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:stroke-accent',
          ].join(' '),
        };
        return p.kind === 'path' ? (
          <path key={p.id} d={p.shape} {...common}>
            <title>{p.title}</title>
          </path>
        ) : (
          <polygon key={p.id} points={p.shape} {...common}>
            <title>{p.title}</title>
          </polygon>
        );
      })}
    </svg>
  );
}
